/**
 * Centralized Locale Utilities
 * Reads locale settings from localStorage and provides
 * formatCurrency, formatDate, formatNumber globally.
 */

const COUNTRY_PRESETS = {
  GH: { name: 'Ghana',          code: 'GHS', symbol: 'GH₵', dateFormat: 'DD/MM/YYYY', numberLocale: 'en-GH',  tz: 'Africa/Accra' },
  US: { name: 'United States',  code: 'USD', symbol: '$',    dateFormat: 'MM/DD/YYYY', numberLocale: 'en-US',  tz: 'America/New_York' },
  GB: { name: 'United Kingdom', code: 'GBP', symbol: '£',    dateFormat: 'DD/MM/YYYY', numberLocale: 'en-GB',  tz: 'Europe/London' },
  NG: { name: 'Nigeria',        code: 'NGN', symbol: '₦',    dateFormat: 'DD/MM/YYYY', numberLocale: 'en-NG',  tz: 'Africa/Lagos' },
  KE: { name: 'Kenya',          code: 'KES', symbol: 'KSh',  dateFormat: 'DD/MM/YYYY', numberLocale: 'en-KE',  tz: 'Africa/Nairobi' },
  ZA: { name: 'South Africa',   code: 'ZAR', symbol: 'R',    dateFormat: 'YYYY/MM/DD', numberLocale: 'en-ZA',  tz: 'Africa/Johannesburg' },
  IN: { name: 'India',          code: 'INR', symbol: '₹',    dateFormat: 'DD/MM/YYYY', numberLocale: 'en-IN',  tz: 'Asia/Kolkata' },
  AE: { name: 'UAE',            code: 'AED', symbol: 'AED',  dateFormat: 'DD/MM/YYYY', numberLocale: 'ar-AE',  tz: 'Asia/Dubai' },
  SA: { name: 'Saudi Arabia',   code: 'SAR', symbol: 'SAR',  dateFormat: 'DD/MM/YYYY', numberLocale: 'ar-SA',  tz: 'Asia/Riyadh' },
  CA: { name: 'Canada',         code: 'CAD', symbol: 'CA$',  dateFormat: 'YYYY-MM-DD', numberLocale: 'en-CA',  tz: 'America/Toronto' },
  AU: { name: 'Australia',      code: 'AUD', symbol: 'A$',   dateFormat: 'DD/MM/YYYY', numberLocale: 'en-AU',  tz: 'Australia/Sydney' },
  DE: { name: 'Germany',        code: 'EUR', symbol: '€',    dateFormat: 'DD.MM.YYYY', numberLocale: 'de-DE',  tz: 'Europe/Berlin' },
  FR: { name: 'France',         code: 'EUR', symbol: '€',    dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-FR',  tz: 'Europe/Paris' },
  JP: { name: 'Japan',          code: 'JPY', symbol: '¥',    dateFormat: 'YYYY/MM/DD', numberLocale: 'ja-JP',  tz: 'Asia/Tokyo' },
  CN: { name: 'China',          code: 'CNY', symbol: '¥',    dateFormat: 'YYYY-MM-DD', numberLocale: 'zh-CN',  tz: 'Asia/Shanghai' },
  BR: { name: 'Brazil',         code: 'BRL', symbol: 'R$',   dateFormat: 'DD/MM/YYYY', numberLocale: 'pt-BR',  tz: 'America/Sao_Paulo' },
  MX: { name: 'Mexico',         code: 'MXN', symbol: 'MX$',  dateFormat: 'DD/MM/YYYY', numberLocale: 'es-MX',  tz: 'America/Mexico_City' },
  EG: { name: 'Egypt',          code: 'EGP', symbol: 'E£',   dateFormat: 'DD/MM/YYYY', numberLocale: 'ar-EG',  tz: 'Africa/Cairo' },
  PK: { name: 'Pakistan',       code: 'PKR', symbol: 'Rs',   dateFormat: 'DD/MM/YYYY', numberLocale: 'en-PK',  tz: 'Asia/Karachi' },
  BD: { name: 'Bangladesh',     code: 'BDT', symbol: '৳',    dateFormat: 'DD/MM/YYYY', numberLocale: 'bn-BD',  tz: 'Asia/Dhaka' },
  PH: { name: 'Philippines',    code: 'PHP', symbol: '₱',    dateFormat: 'MM/DD/YYYY', numberLocale: 'en-PH',  tz: 'Asia/Manila' },
  TZ: { name: 'Tanzania',       code: 'TZS', symbol: 'TSh',  dateFormat: 'DD/MM/YYYY', numberLocale: 'sw-TZ',  tz: 'Africa/Dar_es_Salaam' },
  UG: { name: 'Uganda',         code: 'UGX', symbol: 'USh',  dateFormat: 'DD/MM/YYYY', numberLocale: 'en-UG',  tz: 'Africa/Kampala' },
  RW: { name: 'Rwanda',         code: 'RWF', symbol: 'RF',   dateFormat: 'DD/MM/YYYY', numberLocale: 'rw-RW',  tz: 'Africa/Kigali' },
  SG: { name: 'Singapore',      code: 'SGD', symbol: 'S$',   dateFormat: 'DD/MM/YYYY', numberLocale: 'en-SG',  tz: 'Asia/Singapore' },
  MY: { name: 'Malaysia',       code: 'MYR', symbol: 'RM',   dateFormat: 'DD/MM/YYYY', numberLocale: 'ms-MY',  tz: 'Asia/Kuala_Lumpur' },
  // XOF - Francophone West Africa (UEMOA)
  SN: { name: 'Senegal',        code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-SN',  tz: 'Africa/Dakar' },
  CI: { name: "Côte d'Ivoire",  code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-CI',  tz: 'Africa/Abidjan' },
  ML: { name: 'Mali',           code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-ML',  tz: 'Africa/Bamako' },
  BF: { name: 'Burkina Faso',   code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-BF',  tz: 'Africa/Ouagadougou' },
  NE: { name: 'Niger',          code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-NE',  tz: 'Africa/Niamey' },
  TG: { name: 'Togo',           code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-TG',  tz: 'Africa/Lome' },
  BJ: { name: 'Benin',          code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-BJ',  tz: 'Africa/Porto-Novo' },
  GW: { name: 'Guinea-Bissau',  code: 'XOF', symbol: 'CFA',  dateFormat: 'DD/MM/YYYY', numberLocale: 'pt-GW',  tz: 'Africa/Bissau' },
  // XAF - Francophone Central Africa (CEMAC)
  CM: { name: 'Cameroon',       code: 'XAF', symbol: 'FCFA', dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-CM',  tz: 'Africa/Douala' },
  GA: { name: 'Gabon',          code: 'XAF', symbol: 'FCFA', dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-GA',  tz: 'Africa/Libreville' },
  CG: { name: 'Congo (Brazzaville)', code: 'XAF', symbol: 'FCFA', dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-CG', tz: 'Africa/Brazzaville' },
  TD: { name: 'Chad',           code: 'XAF', symbol: 'FCFA', dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-TD',  tz: 'Africa/Ndjamena' },
  CF: { name: 'Central African Rep.', code: 'XAF', symbol: 'FCFA', dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-CF', tz: 'Africa/Bangui' },
  GQ: { name: 'Equatorial Guinea', code: 'XAF', symbol: 'FCFA', dateFormat: 'DD/MM/YYYY', numberLocale: 'fr-GQ', tz: 'Africa/Malabo' },
};

const DEFAULT_LOCALE = {
  country: 'GH',
  currency_code: 'GHS',
  currency_symbol: 'GH₵',
  date_format: 'DD/MM/YYYY',
  number_locale: 'en-GH',
  timezone: 'Africa/Accra',
};

function getLocale() {
  try {
    const s = JSON.parse(localStorage.getItem('pharmxai_settings') || '{}');
    const locale = s.locale || {};
    return { ...DEFAULT_LOCALE, ...locale };
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Format a number as currency with the configured symbol and locale
 */
export function fmtCurrency(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) return `${getLocale().currency_symbol} 0.00`;
  const locale = getLocale();
  const num = Number(amount);
  try {
    const formatted = num.toLocaleString(locale.number_locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${locale.currency_symbol} ${formatted}`;
  } catch {
    return `${locale.currency_symbol} ${num.toFixed(2)}`;
  }
}

/**
 * Format a date string according to the configured date format
 */
export function fmtDate(dateStr) {
  if (!dateStr) return '';
  const locale = getLocale();
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (locale.date_format) {
      case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
      case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
      case 'YYYY/MM/DD': return `${yyyy}/${mm}/${dd}`;
      case 'DD.MM.YYYY': return `${dd}.${mm}.${yyyy}`;
      case 'DD/MM/YYYY':
      default: return `${dd}/${mm}/${yyyy}`;
    }
  } catch {
    return dateStr;
  }
}

/**
 * Format a number with locale-appropriate thousands/decimal separators
 */
export function fmtNumber(num) {
  if (num === null || num === undefined || isNaN(num)) return '0';
  const locale = getLocale();
  try {
    return Number(num).toLocaleString(locale.number_locale);
  } catch {
    return Number(num).toLocaleString();
  }
}

/**
 * Get just the currency symbol (for templates and inline use)
 */
export function getCurrencySymbol() {
  return getLocale().currency_symbol;
}

/**
 * Get the full locale settings object
 */
export function getLocaleSettings() {
  return getLocale();
}

/**
 * Get all available country presets
 */
export function getCountryPresets() {
  return COUNTRY_PRESETS;
}

export { COUNTRY_PRESETS, DEFAULT_LOCALE };
